import {Module} from "vuex";
import Vue from "vue";
import {State} from "@/store";
import {Permissions} from "@/helpers/constants";
import UserSimple from "@/models/user/UserSimple";
import CompanySimple from "@/models/company/CompanySimple";

export interface UserState {
	token: string | null;
	refreshToken: string | null;
	isRefreshingToken: boolean;

	loggedInUser: UserSimple | null;
	loggedInUserCompany: CompanySimple | null;
}

const user: Module<UserState, State> = {
	namespaced: true,

	state: {
		token: null,
		refreshToken: null,
		isRefreshingToken: false,
		loggedInUser: null,
		loggedInUserCompany: null
	},
	getters: {
		isLoggedIn: (state): boolean => {
			return state.token !== null;
		},
		userId: (state, getters) => {
			const tokenData = getters.tokenData;
			if (null === tokenData) {
				return null;
			}
			return tokenData.sub;
		},
		model: (state): UserSimple | null => {
			return state.loggedInUser;
		},
		permissions: (state, getters): string[] => {
			try {
				const permissions = getters.tokenData['portal.top-drivers.ch.permission'];
				return Array.isArray(permissions) ? permissions : [permissions];
			} catch (e) {
				return [];
			}
		},
		hasPermission: (state, getters) => (permission: string): boolean => {
			const permissions = getters.permissions as string[];
			return permissions.includes(permission);
		},
		isAdmin: (state, getters): boolean => {
			return getters.hasPermission(Permissions.rightAdmin);
		},
		roles: (state, getters): string[] => {
			try {
				const roles = getters.tokenData['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
				return Array.isArray(roles) ? roles : [roles];
			} catch (e) {
				return [];
			}
		},
		hasRole: (state, getters) => (role: string): boolean => {
			const roles = getters.roles as string[];
			return roles.includes(role);
		},
		tokenData: state => {
			if (null === state.token) {
				return null;
			}
			return JSON.parse(window.atob(state.token.split('.')[1]))
		},
	},
	mutations: {
		setTokens(state, payload: { token: string; refreshToken: string }) {
			state.token = payload.token;
			state.refreshToken = payload.refreshToken;
		},
		setIsRefreshingToken(state, payload: boolean) {
			state.isRefreshingToken = payload;
		},
		setLoggedInUser(state, payload: UserSimple | null) {
			state.loggedInUser = payload;
			state.loggedInUserCompany = payload?.company ?? null;
		},
	},
	actions: {
		async refreshToken(context) {
			if (null == context.state.refreshToken) {
				return;
			}

			context.commit('setIsRefreshingToken', true);
			try {
				const newToken = await Vue.$authService.refreshToken({
					refreshToken: context.state.refreshToken,
					token: context.state.token ?? '',
				});

				context.commit('setTokens', newToken);
				await context.dispatch('loadLoggedInUserData');
			} catch (err) {
				console.error('error refreshing token', err);
				await context.dispatch('logout');
			}

			context.commit('setIsRefreshingToken', false);
		},
		async logout(context, payload: { userId: string } | null) {
			try {
				await Vue.$authService.logout();
			} catch (e) {
				console.error('logout failed', e);
			}

			if (null === payload || undefined === payload || null === payload.userId) {
				context.commit('setTokens', {token: null, refreshToken: null});
				await context.dispatch('loadLoggedInUserData');
			}
		},
		async loadLoggedInUserData(context) {
			if (null == context.state.token) {
				context.commit('setLoggedInUser', null);
				return;
			}

			try {
				const user = await Vue.$userService.user(context.getters.userId);
				context.commit('setLoggedInUser', user);
				context.commit('ui/setLocale', user.languageCode, {root: true});
			} catch (e) {
				console.error('fetching logged in user data failed', e);
			}
		},
	},
};

export default user;
